import { useState, useEffect } from "react";
import { Box, MenuItem, Stack, Typography, ListItemText } from "@mui/material";
import { CustomCheckbox } from "../CustomCheckbox";
import { ExportCompany } from "../../../types/apiTypes";
import { ListItem } from "./ListItem";
import { CustomInput } from "../CustomInput";
import { SearchIcon } from "../../images/SearchIcon";
import { useDebounceValue, useVirtualizedList } from "../../../hooks";
import { useElementSize } from "hooks/useElementSize";

const ITEM_HEIGHT = 50;
const VIEWPORT_HEIGHT = 240;

type Props = {
  value: string;
  setValue: (value: string) => void;
  companyList: ExportCompany[];
  selectAllHandler: () => void;
  analyzedItemList: string[];
  selectedItemList: string[];
  handleChange: (item: string) => void;
};

export const SelectCompanies = ({
  value,
  setValue,
  companyList,
  selectAllHandler,
  selectedItemList,
  analyzedItemList,
  handleChange,
}: Props) => {
  const [containerRef, { height: viewportHeight }] =
    useElementSize<HTMLDivElement>();
  const [searchValue, setSearchValue] = useState<string>(value);
  const searchValueDebounce = useDebounceValue(searchValue);

  useEffect(() => {
    setValue(searchValueDebounce);
  }, [searchValueDebounce]);

  const {
    visibleItems,
    startIndex,
    containerHeight,
    handleScroll,
    filteredItems,
  } = useVirtualizedList<ExportCompany>({
    items: companyList,
    itemHeight: ITEM_HEIGHT,
    viewportHeight: viewportHeight,
    searchValue: searchValueDebounce,
    filterFn: (item, search) =>
      item.name.toLowerCase().includes(search.toLowerCase()),
  });

  return (
    <Stack paddingY="8px" gap="8px">
      <Stack gap="8px">
        <Typography variant="body1" color="text.secondary">
          Please choose data sources you want to export
        </Typography>
        <CustomInput
          value={searchValue}
          placeholder="Search"
          prefixIcon={<SearchIcon />}
          onChange={setSearchValue}
          clearButton
        />
      </Stack>

      <Box
        ref={containerRef}
        onScroll={() => {
          if (containerRef.current) {
            handleScroll(containerRef.current.scrollTop);
          }
        }}
        sx={{
          position: "relative",
          overflowY: "auto",
          maxHeight: `${VIEWPORT_HEIGHT}px`,
        }}
      >
        {filteredItems.length > 0 && !searchValueDebounce && (
          <MenuItem onClick={selectAllHandler} sx={{ padding: "4px" }}>
            <CustomCheckbox
              checked={
                selectedItemList.length > 0 &&
                selectedItemList.length === analyzedItemList.length
              }
            />
            <ListItemText primary="Select all" />
          </MenuItem>
        )}

        <Box
          sx={{
            height: `${containerHeight}px`,
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: `${startIndex * ITEM_HEIGHT}px`,
              left: 0,
              right: 0,
            }}
          >
            {visibleItems.map((el) => (
              <ListItem
                key={el.id}
                isSelected={selectedItemList.includes(el.id)}
                handleChange={handleChange}
                company={el}
              />
            ))}
          </Box>
        </Box>

        {filteredItems.length === 0 && searchValueDebounce && (
          <Typography
            component="span"
            variant="body1"
            color="text.secondary"
            pl="12px"
          >
            No company with such name found
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
